import { FC, ReactNode } from 'react';
import { Avatar, Button, Divider } from 'antd';
import { TbEdit, TbForms, TbNumbers } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { SmallTag } from '@/shared/components/tags';
import { SmallTooltip } from '@/shared/components/tooltips';

interface ReportComponentReadinessOverlayProps {
    appearance?: 'default' | 'compact';
    compactAlignment?: 'left' | 'center';
    configureDisabled?: boolean;
    description?: ReactNode;
    icon?: ReactNode;
    missing?: string[];
    onConfigure?: () => void;
    title?: ReactNode;
}

const humanizeRequirement = (requirement: string) =>
    requirement.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (character) => character.toUpperCase());

const ReportComponentReadinessOverlay: FC<ReportComponentReadinessOverlayProps> = ({
    appearance = 'default',
    compactAlignment = 'left',
    configureDisabled = false,
    description,
    icon,
    missing = [],
    onConfigure,
    title,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const missingRequirements = missing.map(humanizeRequirement).join(', ');
    const compactCentered = compactAlignment === 'center';

    if (appearance === 'compact') {
        return (
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: compactCentered ? 'center' : undefined,
                    backgroundColor: token.colorBgContainer,
                    overflow: 'hidden',
                    padding: '0.5rem 0.75rem',
                }}
            >
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: compactCentered ? 'column' : 'row',
                        gap: '0.5rem',
                        justifyContent: compactCentered ? 'center' : undefined,
                        maxWidth: '100%',
                        minWidth: 0,
                        width: compactCentered ? 'fit-content' : '100%',
                    }}
                >
                    <Avatar
                        shape='square'
                        size={28}
                        style={{ border: `1px dashed ${token.colorBorder}`, flex: '0 0 auto' }}
                        icon={<TbNumbers size='75%' style={{ color: token.colorTextTertiary }} />}
                    />
                    <div
                        style={{
                            flex: compactCentered ? '0 1 auto' : '1 1 auto',
                            minWidth: 0,
                            textAlign: compactCentered ? 'center' : 'left',
                        }}
                    >
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <small style={{ color: token.colorTextSecondary, fontWeight: token.fontWeightStrong }}>
                                {title ?? 'Configuration Required'}
                            </small>
                        </div>
                        <div
                            title={missingRequirements}
                            style={{
                                display: 'flex',
                                gap: '0.25rem',
                                justifyContent: compactCentered ? 'center' : 'flex-start',
                                marginTop: '0.25rem',
                                overflow: 'hidden',
                            }}
                        >
                            {missing.map((requirement) => (
                                <SmallTag
                                    key={requirement}
                                    message={humanizeRequirement(requirement)}
                                    styles={{ flex: '0 0 auto', marginLeft: 0, whiteSpace: 'nowrap' }}
                                />
                            ))}
                        </div>
                    </div>
                    {onConfigure && (
                        <SmallTooltip title='Configure Statistic'>
                            <Button
                                aria-label='Configure Statistic'
                                disabled={configureDisabled}
                                icon={<TbEdit />}
                                onClick={onConfigure}
                                size='small'
                                style={{ flex: '0 0 auto' }}
                                type='dashed'
                            />
                        </SmallTooltip>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '1rem',
                backdropFilter: 'blur(0.5rem)',
                WebkitBackdropFilter: 'blur(0.5rem)',
                backgroundColor: token.colorBgBlur,
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius * 1.5,
                    boxShadow: token.boxShadow,
                }}
            >
                <section style={{ padding: '1rem 1.5rem' }}>
                    <Avatar
                        shape='square'
                        style={{ border: `1px solid ${token.colorBorder}` }}
                        size={40}
                        icon={icon ?? <TbForms size='75%' style={{ color: token.colorTextTertiary }} />}
                    />
                    <div style={{ margin: '0.5rem 0 0.25rem' }}>
                        <strong>{title ?? 'Configuration Required'}</strong>
                    </div>
                    {description || missing.length > 0 ? (
                        <div style={{ marginBottom: missing.length > 0 ? '0.5rem' : 0 }}>
                            <small style={{ color: token.colorTextTertiary }}>
                                {description ?? 'Set the Following Properties'}
                            </small>
                        </div>
                    ) : null}
                    {missing.length > 0 ? (
                        <div>
                            {missing.map((requirement) => (
                                <SmallTag key={requirement} message={humanizeRequirement(requirement)} />
                            ))}
                        </div>
                    ) : null}
                </section>
                {onConfigure && (
                    <>
                        <Divider style={{ margin: 0 }} />
                        <section style={{ padding: '1rem 1.5rem' }}>
                            <Button
                                size='small'
                                icon={<TbEdit />}
                                onClick={onConfigure}
                                type='dashed'
                                disabled={configureDisabled}
                            >
                                Configure
                            </Button>
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

export default ReportComponentReadinessOverlay;
